import { useEffect, useRef, useState } from 'react';

import {
  allowedImageSize,
  allowedImageSizeLabel,
  excpectedOrganizationLogoWidth,
  excpectedOrganizationLogoWidthHeight
} from '@constants/file';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';

import Icon from '../icon/Icon';
import Label from '../label/Label';
import ImageCropModal from './ImageCropModal';
import { ImageUploadProps } from './types';

const ImageUploadBranch = (props: ImageUploadProps) => {
  const {
    label,
    imageSrc,
    showDeleteIcon,
    onClearImage,
    onImageChange,
    acceptedImageFormats = 'image/jpeg, image/png,',
    imgClassName,
    subText,
    labelStyle
  } = props;

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string>('');
  const [openImageCropModal, setOpenImageCropModal] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onClear = () => {
    setErrorMessage('');
    setFileName('');
    setFileUrl('');
    setOpenImageCropModal(false);
    if (onClearImage) onClearImage();
  };

  const isValidFile = (selectedFile: File) => {
    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'jpeg') {
      const error = 'Upload failed. Unsupported file type';
      setErrorMessage(error);
      return false;
    }
    if (selectedFile.size > allowedImageSize) {
      const error = `Please select a file smaller than ${allowedImageSizeLabel}.`;
      setErrorMessage(error);
      sendMixpanelEvent(Event.IMAGE_UPLOAD_ERROR, {
        validation_error: `Image size exceeded ${allowedImageSizeLabel}`,
        image_type: label
      });
      return false;
    }
    return true;
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPhoto = e?.target?.files?.[0];
    if (selectedPhoto && isValidFile(selectedPhoto)) {
      setErrorMessage('');
      setFileName(selectedPhoto.name);
      setFileUrl(URL.createObjectURL(selectedPhoto));
      setOpenImageCropModal(true);
    }
  };

  const onCancelCrop = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setFileUrl('');
    setFileName('');
    setOpenImageCropModal(false);
  };

  const onConfirmCrop = (croppedFile: File) => {
    const previewUrl = URL.createObjectURL(croppedFile);
    onImageChange({ file: croppedFile, preview: previewUrl });
    setFileUrl('');
    setFileName('');
    setOpenImageCropModal(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  useEffect(
    () => () => {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
    },
    [fileUrl]
  );

  const renderImageSelection = () => (
    <div className="flex">
      <input
        type="file"
        id="image-input"
        className="hidden"
        accept={acceptedImageFormats}
        onChange={onChange}
        ref={inputRef}
      />
      <label htmlFor="image-input" className="flex cursor-pointer self-center">
        <Icon name="photo-upload" size="none" className="size-22" />
      </label>
    </div>
  );

  return (
    <div className="relative">
      <Label label={label} optionalText={subText} className={labelStyle} />

      {imageSrc ? (
        <div
          className="relative flex w-fit"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <label htmlFor="image-input" className="cursor-pointer self-center">
            <img
              src={imageSrc}
              className={`
                size-20 rounded-2xl border border-greyStroke
                ${imgClassName} ${isHovered ? 'opacity-50' : 'opacity-100'}`}
              alt="preview"
            />
          </label>
          {showDeleteIcon && (
            <div
              className="absolute -bottom-2 left-16 flex size-26 cursor-pointer items-center justify-center rounded-full border border-grey bg-grey10 p-1.5"
              onClick={onClear}
              role="button"
              tabIndex={0}
              aria-label="Remove image"
              onKeyDown={(e) => e.key === 'Enter' && onClear()}
            >
              <Icon size="large" name="trash" />
            </div>
          )}
          {isHovered && (
            <label
              htmlFor="image-input"
              className="absolute bottom-7 left-7 flex cursor-pointer items-center justify-center"
            >
              <Icon name="image-upload" size="24" />
            </label>
          )}
          <input
            type="file"
            id="image-input"
            className="hidden"
            accept={acceptedImageFormats}
            onChange={onChange}
            ref={inputRef}
          />
        </div>
      ) : (
        renderImageSelection()
      )}

      {errorMessage && (
        <p className="absolute mt-1 text-xs text-error">{errorMessage}</p>
      )}

      {openImageCropModal && (
        <ImageCropModal
          onClose={onCancelCrop}
          onConfirm={onConfirmCrop}
          fileName={fileName}
          previewUrl={fileUrl || ''}
          isOpen={openImageCropModal}
          expectedAspectRatio={
            excpectedOrganizationLogoWidth /
            excpectedOrganizationLogoWidthHeight
          }
        />
      )}
    </div>
  );
};

export default ImageUploadBranch;
